import { removeEmptyValues as n } from "@partoohub/utils";
import { ApiResourceBaseClass as o } from "./baseApiCalls.js";
const e = "business", r = `${e}/cities`;
class d extends o {
  /**
   * Set photos, open hours or specific hours to several businesses
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/business/bulk-modification`
   *
   * @param {BusinessModalFiltersType} filters - The business modal filters that select a subset of the businesses of the org
   * @param {BulkRequest} data - The new photos, open hours or specific hours
   */
  applyBulkInApp = async (t, s) => this.post(`${e}/bulk-modification`, {
    filters: t,
    data: s
  });
  /**
   * Get the list of countries represented by the selected businesses
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/business/countries`
   *
   * @param {BusinessModalFiltersType} filters - The business modal filters that select a subset of the businesses of the org
   * @returns {Promise<Country[]>} The list of countries represented by these businesses. No country appear more than once.
   */
  countries = async (t) => {
    const { data: s } = await this.post(`${e}/countries`, t);
    return s;
  };
  /**
   * Searches for businesses based on the provided query parameters.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/business/search`
   *
   * @param {SearchBusinessParams} queryParams - The parameters to be used in the business search request.
   * @returns {Promise<SearchBusinessesReceivedData>} A promise that resolves to the data containing the search results.
   */
  searchBusinesses = async (t) => {
    const s = n(t), { data: a } = await this.get(
      `${e}/search`,
      s
    );
    return a;
  };
  /**
   * Retrieves all available cities from the API.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/business/cities`
   *
   * @returns {Promise<Record<'cities',CitiesObject>>} A promise that resolves to the object containing the list of cities.
   */
  getAllCities = async () => {
    const { data: t } = await this.get(r);
    return t;
  };
  /**
   * Lists the users that have access to a given business.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/users/business/:businessId`
   *
   * @param {string} businessId - The id of the business to list the users for.
   * @param {BusinessUsersParams} queryParams - Query parameters to filter the results.
   * @returns {Promise<BusinessUsersAPIResponse>} A promise that resolves to the object containing the list of users, paginated
   */
  getBusinessUsers = async (t, s) => {
    const { data: a } = await this.get(
      `users/business/${t}`,
      s
    );
    return a;
  };
  /**
   * Gets the business edit fields
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/business/{businessId}/business_fields`
   *
   * @param {string} businessId - The ID of the business
   * @returns {Promise<BusinessFieldsType>} The business edit fields data
   */
  getBusinessEditFields = async (t) => {
    const { data: s } = await this.get(
      `${e}/${t}/business_fields`
    );
    return s;
  };
  /**
   * Searches for business statistics based on the provided query parameters.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/business/search/stats`
   *
   * @param {SearchBusinessParams} queryParams - The parameters to be used in the business search stats request.
   * @returns {Promise<SearchBusinessesStatsData>} A promise that resolves to the data containing the search stats results.
   */
  searchBusinessesStats = async (t) => {
    const { data: s } = await this.get(
      `${e}/search/stats`,
      t
    );
    return s;
  };
  /**
   * Searches for the count of businesses based on the provided query parameters.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/business/search/count`
   *
   * @param {SearchBusinessParams} queryParams - The parameters to be used in the business search count request.
   * @returns {Promise<SearchBusinessesCountData>} A promise that resolves to the data containing the count of businesses.
   */
  searchBusinessesCount = async (t) => {
    const s = n(t), { data: a } = await this.get(
      `${e}/search/count`,
      s
    );
    return a;
  };
  /**
   * Retrieves the languages of the businesses based on the provided query parameters.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/business/languages`
   *
   * @param {BusinessModalFiltersType} queryParams - The parameters to be used in the business languages request.
   * @returns {Promise<BusinessLanguagesData>} A promise that resolves to the data containing the languages of the businesses.
   */
  getBusinessesLanguages = async (t) => {
    const { data: s } = await this.get(
      `${e}/languages`,
      t
    );
    return s;
  };
  /**
   * Retrieves the business data for a specific business.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/business/{businessId}`
   *
   * @param {string} businessId - The ID of the business
   * @param {boolean} partoo_ui - A flag indicating whether the request is made from the front-end.
   * @returns {Promise<V2BusinessData>} A promise that resolves to the data containing the business information.
   */
  getBusiness = async (t, s = !1) => {
    const { data: a } = await this.get(
      `${e}/${t}?partoo_ui=${s}`
    );
    return a;
  };
  /**
   * Retrieves the subscriptions for the businesses.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/business/subscriptions`
   *
   * @param {SubscriptionsStatsParams} queryParams - The parameters to be used in the subscriptions request.
   * @returns {Promise<V2Subscriptions>} A promise that resolves to the data containing the subscriptions information.
   */
  getSubscriptions = async (t) => {
    const { data: s } = await this.get(
      `${e}/subscriptions`,
      t
    );
    return s;
  };
  /**
   * Retrieves the user business connections' statistics.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/business/connections_stats`
   *
   * @param {Object} [params={}] - Optional parameters for the connections stats request.
   * @returns {Promise<ConnectionsStatsReceivedData>} A promise that resolves to the data containing the connections statistics.
   */
  getUserBusinessConnectionsStats = async (t = {}) => {
    const { data: s } = await this.get(
      `${e}/connections_stats`,
      t
    );
    return s;
  };
  /**
   * Creates a new business.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/business`
   *
   * @param {BusinessCreationRequestData} businessData - The data for the new business
   * @param {boolean} partoo_ui - A flag indicating whether the request is made from the front-end
   * @returns {Promise<CreationSuccessResponses | CreationFailureResponse>} A promise that resolves to the response of the creation request.
   */
  createBusiness = async (t, s = !1) => {
    const { data: a } = await this.post(
      `${e}?partoo_ui=${s}`,
      t
    );
    return a;
  };
  /**
   * Imports Google My Business information for a specific business.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/business/{businessId}/google-import`
   *
   * @param {string} businessId - The ID of the business
   * @param {FormattedBusinessImportFieldsType} requestData - The data to be imported
   * @returns {Promise<SuccessResponse>} A promise that resolves to the response of the import request.
   */
  importGMBInfos = async (t, s) => {
    const { data: a } = await this.post(
      `${e}/${t}/google-import`,
      s
    );
    return a;
  };
  /**
   * Updates the business data for a specific business.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/business/{businessId}`
   *
   * @param {string} businessId - The ID of the business
   * @param {BusinessUpdateRequestData} businessData - The data to update the business
   * @param {boolean} partoo_ui - A flag indicating whether the request is made from the front-end
   * @returns {Promise<SuccessResponse>} A promise that resolves to the response of the update request.
   */
  updateBusiness = async (t, s, a = !1) => {
    const { data: i } = await this.post(
      `${e}/${t}?partoo_ui=${a}`,
      s
    );
    return i;
  };
  /**
   * Updates the completion rate of a specific business.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/business/{businessId}/completion_rate`
   *
   * @param {string} businessId - The ID of the business
   * @returns {Promise<CompletionRateResponse>} A promise that resolves to the response of the update request.
   */
  updateBusinessCompletionRate = async (t) => {
    const { data: s } = await this.post(
      `${e}/${t}/completion_rate`
    );
    return s;
  };
  /**
   * Updates the more hours data for a specific business.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/business/{businessId}/more_hours`
   *
   * @param {string} businessId - The ID of the business
   * @param {BusinessMoreHoursPostType} moreHoursData - The more hours data to update
   * @returns {Promise<SuccessResponse>} A promise that resolves to the response of the update request.
   */
  updateBusinessMoreHours = async (t, s) => {
    const { data: a } = await this.post(
      `${e}/${t}/more_hours`,
      s
    );
    return a;
  };
  /**
   * Fetches Google My Business images for a specific business.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/business/import-photo/{businessId}`
   *
   * @param {string} businessId - The ID of the business
   * @returns {Promise<FetchGmbImagesReceivedData>} A promise that resolves to the data containing the fetched images.
   */
  fetchGmbImages = async (t) => {
    const { data: s } = await this.post(
      `${e}/import-photo/${t}`
    );
    return s;
  };
  /**
   * Retrieves the business attributes for a specific business.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/business/{businessId}/attributes`
   *
   * @param {string} businessId - The ID of the business
   * @param {BusinessAttributesGetParamsType} queryParams - The parameters to be used in the attributes request.
   * @returns {Promise<BusinessAttributesGetDataType>} A promise that resolves to the data containing the business attributes.
   */
  businessAttributesGet = async (t, s) => {
    const { data: a } = await this.get(
      `${e}/${t}/attributes`,
      s
    );
    return a;
  };
  /**
   * Updates the business attributes for a specific business.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/business/{businessId}/attributes`
   *
   * @param {string} businessId - The ID of the business
   * @param {BusinessAttributesPostParamsType} queryParams - The parameters to be used in the attributes update request.
   * @returns {Promise<SuccessResponse>} A promise that resolves to the response of the update request.
   */
  businessAttributesPost = async (t, s) => {
    const { data: a } = await this.post(
      `${e}/${t}/attributes`,
      s
    );
    return a;
  };
  /**
   * Retrieves the place action links for a specific business.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/business/{businessId}/place_action_links`
   *
   * @param {string} businessId - The ID of the business
   * @returns {Promise<GetPlaceActionLinksForBusinessReceivedData>} A promise that resolves to the data containing the place action links.
   */
  businessPlaceActionLinksGet = async (t) => {
    const { data: s } = await this.get(
      `${e}/${t}/place_action_links`
    );
    return s;
  };
  /**
   * Updates the place action links for a specific business.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/business/{businessId}/place_action_links`
   *
   * @param {string} businessId - The ID of the business
   * @param {PlaceActionLinksPostRequest} placeActionLinkData - The place action links data to update
   * @returns {Promise<SuccessResponse>} A promise that resolves to the response of the update request.
   */
  businessPlaceActionLinksPost = async (t, s) => {
    const { data: a } = await this.post(
      `${e}/${t}/place_action_links`,
      s
    );
    return a;
  };
  /**
   * Retrieves the more hours data for a specific business.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/business/{businessId}/more_hours`
   *
   * @param {string} businessId - The ID of the business
   * @param {BusinessMoreHoursGetParamsType} queryParams - The parameters to be used in the more hours request.
   * @returns {Promise<MoreHoursListReceivedData>} A promise that resolves to the data containing the more hours information.
   */
  businessMoreHoursGet = async (t, s) => {
    const { data: a } = await this.get(
      `${e}/${t}/more_hours`,
      s
    );
    return a;
  };
  /**
   * Creates businesses during the onboarding process.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/onboarding-businesses`
   *
   * @param {Array<BusinessLocation>} locations - The locations of the businesses to be created
   * @param {Array<number>} [groups] - Optional groups for the businesses
   * @returns {Promise<OnboardingCreationSuccessResponses | CreationFailureResponse>} A promise that resolves to the response of the creation request.
   */
  onboardingBusinessesCreation = async (t, s) => {
    const { data: a } = await this.post("onboarding-businesses", { locations: t, groups: s });
    return a;
  };
  /**
   * Retrieves the disabled diffusers for a specific business.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/business/{businessId}/disabled_diffusers`
   *
   * @param {string} businessId - The ID of the business
   * @returns {Promise<BusinessDisabledDiffusersGetDatatype>} A promise that resolves to the data containing the disabled diffusers.
   */
  businessDisabledDiffusersGet = async (t) => {
    const { data: s } = await this.get(
      `${e}/${t}/disabled_diffusers`
    );
    return s;
  };
  /**
   * Retrieves the connected channels for a specific business.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/business/{businessId}/connected_channels`
   *
   * @param {string} businessId - The ID of the business
   * @returns {Promise<BusinessConnectedChannels>} A promise that resolves to the data containing the connected channels.
   */
  getBusinessConnectedChannels = async (t) => {
    const { data: s } = await this.get(
      `${e}/${t}/connected_channels`
    );
    return s;
  };
  /**
   * Creates a Google location from a specific business.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/business/{businessId}/google-location`
   *
   * @param {string} businessId - The ID of the business
   * @param {CreateGoogleLocationFromBusinessType} queryParams - The parameters to be used in the Google location creation request.
   * @returns {Promise<GoogleLocationFromBusinessStatus>} A promise that resolves to the response of the creation request.
   */
  createGoogleLocationFromBusiness = async (t, s) => {
    const { data: a } = await this.post(
      `${e}/${t}/google-location`,
      s
    );
    return a;
  };
  /**
   * Completes the business creation log for a specific business.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/business/{businessId}/creation-log`
   *
   * @param {string} businessId - The ID of the business
   * @param {CompleteBusinessCreationLogType} queryParams - The parameters to be used in the creation log request.
   * @returns {Promise<SuccessResponse>} A promise that resolves to the response of the creation log request.
   */
  completeBusinessCreationLog = async (t, s) => {
    const { data: a } = await this.post(
      `${e}/${t}/creation-log`,
      s
    );
    return a;
  };
  /**
   * Deletes the business creation log for a specific business.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/business/{businessId}/no-creation-log`
   *
   * @param {string} businessId - The ID of the business
   * @returns {Promise<SuccessResponse>} A promise that resolves to the response of the deletion request.
   */
  deleteBusinessCreationLog = async (t) => {
    const { data: s } = await this.post(
      `${e}/${t}/no-creation-log`
    );
    return s;
  };
  /**
   * Checks the availability of booking links for a specific business.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/business/{businessId}/booking-links/availability`
   *
   * @param {string} businessId - The ID of the business
   * @returns {Promise<boolean>} A promise that resolves to the availability of booking links.
   */
  getBookingLinksAvailability = async (t) => {
    const { data: s } = await this.get(
      `${e}/${t}/booking-links/availability`
    );
    return s.available;
  };
}
export {
  e as BUSINESS_RESOURCE_URI,
  d as Business,
  r as CITIES_URI
};
